import { useSelector } from 'react-redux';
import {
  oneErrorOnNotOkFetchye,
  replaceDomainForCacheKey,
  useErrorOnNotOkFetchye,
} from '@americanexpress/fetchye-amex';
import { ignoreHeadersByKey } from 'fetchye';
import {
  getBdaasAirportsUrl,
  getBdaasAuthKey,
} from '@/selectors/bdaasSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { getOneCmsLocaleAirportsUrl } from '@/selectors/oneCmsSelectors';
import {
  getIsMarketWithTranslations,
  getLocaleData,
} from '@/selectors/localeSelectors';
import { getWalletIsLoading, getUserWallet } from '@/selectors/walletSelector';
import { Card } from '@/constants';
import { getCenturionOrActiveToken } from '@/helpers/accountHelper';
import { useCenturionOrActiveToken } from '../memberHooks';

export const useBdaasAirports = () => {
  const { product } = useRouteParams();
  const url = useSelector(getBdaasAirportsUrl(product));
  const isWalletLoading = useSelector(getWalletIsLoading);
  const accountToken = useCenturionOrActiveToken();
  const isCenturionProduct = product === Card.Centurion;

  return useErrorOnNotOkFetchye(
    url,
    {
      mapKeyToCacheKey: replaceDomainForCacheKey('bdaas'),
      mapOptionsToKey: ignoreHeadersByKey(['Authorization']),
      credentials: 'include',
      defer: isCenturionProduct ? !accountToken : isWalletLoading,
      headers: {
        ...(accountToken && {
          account_token: accountToken,
        }),
      },
    },
    {
      reporting: {
        url,
        collectionMethod: 'useBdaasAirports',
        moduleId: 'travel-lounge-finder-root',
      },
    }
  );
};

export const loadBdaasAirports = (product) => async (dispatch, getState) => {
  const url = getBdaasAirportsUrl(product)(getState());
  const bdaasAuthKey = getBdaasAuthKey(getState());
  const wallet = getUserWallet(getState());
  const accountToken = getCenturionOrActiveToken(wallet);

  return dispatch(
    oneErrorOnNotOkFetchye(
      url,
      {
        mapKeyToCacheKey: replaceDomainForCacheKey('bdaas'),
        mapOptionsToKey: ignoreHeadersByKey(['Authorization']),
        credentials: 'include',
        headers: {
          ...(accountToken && {
            account_token: accountToken,
          }),
          ...(bdaasAuthKey && {
            Authorization: `Basic ${Buffer.from(`${bdaasAuthKey}`).toString('base64')}`,
          }),
        },
      },
      {
        reporting: {
          url,
          collectionMethod: 'loadBdaasAirports',
          moduleId: 'travel-lounge-finder-root',
        },
      }
    )
  );
};

export const useOneCmsLocaleAirports = () => {
  const isMarketWithTranslations = useSelector(getIsMarketWithTranslations);
  const { locale } = useSelector(getLocaleData);
  const url = useSelector(getOneCmsLocaleAirportsUrl(locale));
  return useErrorOnNotOkFetchye(
    url,
    {
      defer: !isMarketWithTranslations,
      mapKeyToCacheKey: replaceDomainForCacheKey('oneCms'),
    },
    {
      reporting: {
        url,
        collectionMethod: 'useOneCmsLocaleAirports',
        moduleId: 'travel-lounge-finder-root',
      },
    }
  );
};

export const loadOneCmsLocaleAirports =
  (locale) => async (dispatch, getState) => {
    if (!getIsMarketWithTranslations(getState())) {
      return null;
    }
    const state = getState();
    const activeLocale = state.getIn(['intl', 'activeLocale']) || 'en-US';
    const url = getOneCmsLocaleAirportsUrl(locale || activeLocale)(state);
    return dispatch(
      oneErrorOnNotOkFetchye(
        url,
        {
          mapKeyToCacheKey: replaceDomainForCacheKey('oneCms'),
        },
        {
          reporting: {
            url,
            collectionMethod: 'loadOneCmsLocaleAirports',
            moduleId: 'travel-lounge-finder-root',
          },
        }
      )
    );
  };
